import { differenceInDays, format } from 'date-fns';
import { formatAmountWithCommas, FormatCurrencyForDisplay } from '@unified-checkout/design-system';
import "libs/design-system/src/components/Charts/Chart/tooltip.scss";
import type { ApexOptions } from 'apexcharts';
import type { TFunction } from 'i18next';
import type { IFilters } from '../types/zustand/filters';
import type { IFiltersParams } from '../types/innerLayout/home';
import { formatToISODate } from './dateFormats';

const MAX_LINE_INTERVAL = 15;

export const monthLocales = {
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  ar: ["يناير", "فبراير", "مارس", "ابريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"]
};

const FONT = {
  ar: { fontFamily: '"Cairo", sans-serif', fontWeight: 400, fontSize: "12px" },
  en: { fontFamily: '"Inter", sans-serif', fontWeight: 400, fontSize: "12px" }
};

const prettifiedValue = (value: number) => {
  if (Number.isInteger(value)) return value;

  const decimalPlaces = value.toString().split(".")[1]?.length || 0;

  if (decimalPlaces <= 2) return value;

  return value.toFixed(2);
};

const formatChartValues = (value: number) => {
  const absValue = Math.abs(value);

  if (absValue >= 1000000) {
    const millionValue = value / 1000000;
    return Number.isInteger(millionValue)
      ? `${millionValue}M`
      : `${prettifiedValue(millionValue)}M`;
  } else if (absValue >= 1000) {
    const thousandValue = value / 1000;
    return Number.isInteger(thousandValue)
      ? `${thousandValue}K`
      : `${prettifiedValue(thousandValue)}K`;
  } else {
    return prettifiedValue(value).toString();
  }
};

const convertDigit = (num: number, lang: "en" | "ar") => {
  return num.toLocaleString(lang === "ar" ? "ar-EG" : "en-EN", { useGrouping: false });
};

const dateFormater = (date: string | Date, dataLength: number, lang: "en" | "ar") => {
  const month = monthLocales[lang];
  if (!date || date === "-") {
    return " - ";
  } else {
    let d = new Date(date);
    const outputDate = `${convertDigit(d.getDate(), lang)} ${month[d.getMonth()]}${(dataLength < MAX_LINE_INTERVAL ? (" " + convertDigit(d.getFullYear(), lang)) : "")}`
    return outputDate;
  }
};

export const labelsDatesFormatter = (date: string, timeFormat: string, dataLength: number, lang: "en" | "ar") => {
  const parsedDate = new Date(date);
  const formattedDate = format(parsedDate, timeFormat);

  return timeFormat === "HH:mm" ? formattedDate : dateFormater(formattedDate, dataLength, lang);
};

export const formattedFilters: (filters: IFilters) => IFiltersParams = (filters) => {
  const { startDate, endDate, compareStartDate, compareEndDate, channel, currency, is_live } = filters;

  return {
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    compare_start_date: formatToISODate(compareStartDate),
    compare_end_date: formatToISODate(compareEndDate),
    channel: channel,
    currency: currency,
    is_live: is_live
  }
};

export const getDaysInRange = (startDate: Date, endDate: Date): number => {
  return differenceInDays(endDate, startDate);
};

export const getValidTypesFromRange = (daysInRange: number) => {
  let rangeType = "";
  let timeFormat = "MMM d, yyyy";

  if (daysInRange === 0) {
    rangeType = "t";
    timeFormat = "HH:mm";
  } else if (daysInRange >= 1 && daysInRange <= 30) {
    rangeType = "d";
  } else if (daysInRange > 30 && daysInRange < 365) {
    rangeType = "m";
  } else {
    rangeType = "m";
  }
  return { rangeType, timeFormat };
};

export const getLineChartOptions = (
  lang: 'en' | 'ar',
  rangeType: string,
  timeFormat: string,
  xAxisTicksLength: number,
  currency: { en: string, ar: string },
  // xAxisTicksLength here is the main series length
  //  (main and compare series in all cases are equal in length except
  //  when the user compares today with yesterday because the comparison
  //  between 24h and the available hours today)
  labels?: string[]
) => {
  const isAR = lang === 'ar';

  const labelsLength = labels?.length || 0;
  const labelsInclination = isAR || rangeType === "d" || rangeType === "t" || labelsLength >= MAX_LINE_INTERVAL ? -90 : -45;
  const shouldUseStepSize = labelsLength > MAX_LINE_INTERVAL;
  const stepSize = shouldUseStepSize ? Math.ceil(labelsLength / MAX_LINE_INTERVAL) : 1;

  const options: ApexOptions = {
    xaxis: {
      categories: labels,
      range: xAxisTicksLength,
      tickAmount: xAxisTicksLength,
      labels: {
        rotateAlways: true,
        show: !!labels,
        rotate: labelsInclination,
        // offsetY: isAR ? ((labels?.every((label)=>label.length > MAX_LINE_INTERVAL)|| rangeType === "t") ? 8 : 57) : 0,
        // offsetY: labels?.some((label)=>label.length > 15) ? 80 : 0,
        offsetY: 0,
        style: {
          fontSize: isAR ?
            (rangeType === "t" ? "10px" : "12px")
            :
            (rangeType === "t" ? "8px" : "10px"),
          fontFamily: FONT[lang].fontFamily,
          fontWeight: FONT[lang].fontWeight
        },
        formatter: (value) => {
          const index = labels?.indexOf(value);
          const isOdd = labelsLength % 2 !== 0;
          if (
            index === 0 ||
            (isOdd && index === labelsLength - 1) ||
            !shouldUseStepSize ||
            (index && (index % stepSize === 0))
          ) {
            return value;
          }
          return '';
        },
      },
      tooltip: {
        enabled: false,
      }
    },
    yaxis: {
      labels: {
        formatter: val => formatChartValues(val),
        offsetX: isAR ? -10 : 0,
        style: {
          fontFamily: FONT[lang].fontFamily,
          fontSize: FONT[lang].fontSize,
          fontWeight: FONT[lang].fontWeight
        },
      },
      opposite: isAR
    },
    tooltip: {
      custom: function ({ dataPointIndex, w: info }) {
        const formattedCurrency = `<span data-currency-symbol>${`${FormatCurrencyForDisplay(currency[lang], true)}`}</span>`;
        return `
        <div class="ds-charts-tooltip"> 
          <div class="ds-charts-tooltip__header">${labels?.[dataPointIndex]}</div>
          <div class="ds-charts-tooltip__body">
            ${info.config.series.map((s: any, i: number) =>
          `<div class="ds-charts-tooltip__body__item" key='line-series-${i}-key'>
                  <div class="ds-charts-tooltip__body__item__color-label" style="background-color:${info.config.colors[i]};"></div>
                  ${lang === 'ar' ? formattedCurrency : ''}
                  ${s.name}: ${formatAmountWithCommas({ amount: s.data[dataPointIndex], keepDecimals: true, currency: currency.en, excludeZeroDecimals: true })}
                  ${lang === 'en' ? formattedCurrency : ''}
              </div>`
        ).join('')}
          </div>
        </div>
        `
      },
    },
    grid: {
      padding: {
        bottom: labels?.some((label) => label.length > 10) ?
          (isAR ? -20 : -15)
          :
          (isAR ? 5 : 10)
      }
    },
  };

  return options;
};

export const getBarChartOptions = (
  t: TFunction,
  lang: 'en' | 'ar',
  labels: string[] = [],
  currency: { en: string, ar: string },
  daysInRange: number,
) => {
  const isAR = lang === 'ar';
  const options: ApexOptions = {
    xaxis: {
      categories: (!labels || labels.length === 0) ? [''] : labels,
      labels: {
        rotate: -45,
        rotateAlways: true,
        offsetY: 3,
        offsetX: 2,
        // offsetY: isAR && daysInRange < 365 && daysInRange >= 29 ? 40 : 0,
        // offsetX: isAR && daysInRange < 365 && daysInRange >= 29 ? -15 : 0,
        style: {
          fontSize: FONT[lang].fontSize,
          fontFamily: FONT[lang].fontFamily,
          fontWeight: FONT[lang].fontWeight
        },
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      }
    },
    yaxis: {
      labels: {
        formatter: val => formatChartValues(val),
        offsetX: isAR ? -10 : 0,
        style: {
          fontFamily: FONT[lang].fontFamily,
          fontSize: FONT[lang].fontSize,
          fontWeight: FONT[lang].fontWeight
        },
      },
      opposite: isAR
    },
    grid: {
      padding: {
        bottom: isAR ? -10 : -24,
      }
    },
    tooltip: {
      shared: false,
      intersect: true,
      custom: ({ series, dataPointIndex, w: info }) => {

        const currentValue = series[0][dataPointIndex];
        const previousValue = series[1][dataPointIndex];
        const formattedCurrency = `<span data-currency-symbol>${`${FormatCurrencyForDisplay(currency[lang], true)}`}</span>`;

        return `
          <div class="ds-charts-tooltip"> 
            <div class="ds-charts-tooltip__header">${info.globals.labels[dataPointIndex]}</div>
            <div class="ds-charts-tooltip__body">
              <div class="ds-charts-tooltip__body__item" key='bar-series-1'>
                  <div class="ds-charts-tooltip__body__item__color-label" style="background-color:${info.globals.colors[0]};"></div>
                  ${
                    lang === 'en' ?
                      `${t('innerLayout.homePage.charts.currentPeriod')}: ${currentValue ? formatAmountWithCommas({ amount: currentValue, keepDecimals: true, currency: currency.en, excludeZeroDecimals: true }) : 0}${formattedCurrency}`
                      :
                      ''
                  }
                  ${
                    lang === 'ar' ?
                      `${formattedCurrency}${currentValue ? formatAmountWithCommas({ amount: currentValue, keepDecimals: true, currency: currency.en, excludeZeroDecimals: true }) : 0} :${t('innerLayout.homePage.charts.currentPeriod')}`
                      :
                      ''
                  }
              </div>
              <div class="ds-charts-tooltip__body__item" key='bar-series-2'>
                  <div class="ds-charts-tooltip__body__item__color-label" style="background-color:${info.globals.colors[1]};"></div>
                   ${
                    lang === 'en' ?
                      `${t('innerLayout.homePage.charts.previousPeriod')}: ${previousValue ? formatAmountWithCommas({ amount: previousValue, keepDecimals: true, currency: currency.en, excludeZeroDecimals: true }) : 0}${formattedCurrency}`
                      :
                      ''
                  }
                    ${
                    lang === 'ar' ?
                      `${formattedCurrency}${previousValue ? formatAmountWithCommas({ amount: previousValue, keepDecimals: true, currency: currency.en, excludeZeroDecimals: true }) : 0} :${t('innerLayout.homePage.charts.previousPeriod')}`
                      :
                      ''
                  }
              </div>
            </div>
          </div>
          `
      }
    },
    plotOptions: {
      bar: {
        columnWidth: labels?.length ?
          labels?.length <= 4 ?
            `${labels.length}0%`
            :
            "40%"
          :
          "0%"
      }
    },
  };

  return options;
};

export const getDonutChartOptions = (
  t: TFunction,
  lang: 'en' | 'ar',
  setCenterLabel: Function,
  IDS: { main: string, compare: string },
  mainSeries: number[],
  compareSeries: number[],
) => {
  const isAR = lang === 'ar';

  const responsiveRules = [{
    breakpoint: 2500,
    options: {
      chart: {
        width: "90%",
      }
    },
  }, {
    breakpoint: 1500,
    options: {
      chart: {
        width: "85%",
      }
    },
  }, {
    breakpoint: 1350,
    options: {
      chart: {
        width: "80%",
      }
    },
  }, {
    breakpoint: 1250,
    options: {
      chart: {
        width: "75%",
      }
    },
  }, {
    breakpoint: 1200,
    options: {
      chart: {
        width: "70%",
      }
    },
  }, {
    breakpoint: 1150,
    options: {
      chart: {
        width: "60%",
      }
    },
  }, {
    breakpoint: 1050,
    options: {
      chart: {
        width: "55%",
      }
    },
  },
  {
    breakpoint: 980,
    options: {
      chart: {
        width: "45%",
      }
    },
  }, {
    breakpoint: 900,
    options: {
      chart: {
        width: "90%",
      }
    },
  }, {
    breakpoint: 650,
    options: {
      chart: {
        width: "80%",
      }
    },
  }, {
    breakpoint: 600,
    options: {
      chart: {
        width: "75%",
      }
    },
  }, {
    breakpoint: 550,
    options: {
      chart: {
        width: "70%",
      }
    },
  }, {
    breakpoint: 500,
    options: {
      chart: {
        width: "60%",
      }
    },
  }, {
    breakpoint: 450,
    options: {
      chart: {
        width: "50%",
      }
    },
  }, {
    breakpoint: 400,
    options: {
      chart: {
        width: "45%",
      }
    },
  }, {
    breakpoint: 350,
    options: {
      chart: {
        width: "40%",
      }
    },
  }];

  const options: ApexOptions = {
    chart: {
      events: {
        dataPointMouseLeave: (_, chartContext) => {
          const chartID = chartContext.ctx.w.globals.chartID;
          setCenterLabel((prevState: any) => {
            const key = chartID === IDS.main ? 'main' : 'compare';
            return {
              ...prevState,
              [key]: ''
            }
          });
        },
      }
    },
    tooltip: {
      custom: ({ series, seriesIndex, w: info }) => {

        const isNoData = mainSeries?.length === 0;
        const isMainSeriesEmpty = mainSeries?.length > 0 && mainSeries?.every(item => item === 0);
        const isCompareSeriesEmpty = compareSeries?.length > 0 && compareSeries?.every(item => item === 0);

        const mainValue = (mainSeries || series)[seriesIndex];
        const compareValue = (compareSeries || series)[seriesIndex];

        const mainTotal = (mainSeries || series)?.reduce((acc: number, a: number) => acc + a, 0);
        const compareTotal = (compareSeries || series)?.reduce((acc: number, a: number) => acc + a, 0);

        const mainPercentage = mainValue === 0 ? "" : `(${formatAmountWithCommas({ amount: ((mainValue / mainTotal) * 100), keepDecimals: true, excludeZeroDecimals: true })}%)`;
        const comparePercentage = compareValue === 0 ? "" : `(${formatAmountWithCommas({ amount: ((compareValue / compareTotal) * 100), keepDecimals: true, excludeZeroDecimals: true })}%)`;

        const key = info.globals.chartID === IDS.main ? 'main' : 'compare';

        setCenterLabel((prevState: any) => {
          return {
            ...prevState,
            ...(key === 'main' ?
              { [key]: isMainSeriesEmpty ? '' : (info.config.labels[seriesIndex] || '') }
              :
              { [key]: isCompareSeriesEmpty ? '' : (info.config.labels[seriesIndex] || '') }
            )
          }
        });

        return `
          <div class="ds-charts-tooltip"> 
            ${isNoData ?
            `<div class="ds-charts-tooltip__empty-state">${t('innerLayout.homePage.charts.emptyStates.noData')}</div>`
            :
            (
              (
                (key === 'main' && isMainSeriesEmpty) ||
                (key === 'compare' && isCompareSeriesEmpty)
              ) ?
                `<div class="ds-charts-tooltip__empty-state">${t('innerLayout.homePage.charts.emptyStates.noValues')}</div>`
                :
                `<div class="ds-charts-tooltip__header">${info.config.labels[seriesIndex]}</div>
            <div class="ds-charts-tooltip__body">
              <div class="ds-charts-tooltip__body__item" key='bar-series-1'>
                  ${t('innerLayout.homePage.charts.currentPeriod')}: 
                  ${mainValue}
                  ${mainPercentage}
              </div>
              <div class="ds-charts-tooltip__body__item" key='bar-series-2'>
                  ${t('innerLayout.homePage.charts.previousPeriod')}: 
                  ${compareValue}
                  ${comparePercentage}
              </div>
            </div>`
            )

          }
          </div>
          `
      },
      y: {
        formatter: value => (value === 1 ? "0" : value.toString())
      },
    },
    responsive: responsiveRules,
  };

  return options;
};